import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const FUNCTION_NAME = 'gmail-domain-audit';

export const useEmailDomainObservations = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [auditRunning, setAuditRunning] = useState(false);
  const [lastRunSummary, setLastRunSummary] = useState(null);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from('email_domain_observations')
        .select(`
          domain, total_email_count, last_30d_count, last_90d_count,
          first_seen_at, last_seen_at,
          sample_subjects, sample_senders,
          empresa_id, tracking_status, not_applicable_reason,
          audited_colaboradores, last_audited_at, updated_by, updated_at,
          empresas:empresa_id ( id, empresa, company_name )
        `)
        .order('last_30d_count', { ascending: false });
      if (err) throw err;
      setRows(data || []);
    } catch (err) {
      console.error('Erro ao buscar email_domain_observations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const runAudit = async ({ colaborador_ids, days_back } = {}) => {
    setAuditRunning(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.functions.invoke(FUNCTION_NAME, {
        body: {
          colaborador_ids: colaborador_ids && colaborador_ids.length > 0 ? colaborador_ids : undefined,
          days_back: days_back || 365,
          dry_run: false,
        },
      });
      if (err) throw err;
      if (!data?.ok) throw new Error(data?.error || 'Falha ao executar auditoria');
      setLastRunSummary(data.data);
      await fetchRows();
      return { success: true, summary: data.data };
    } catch (err) {
      console.error('Erro audit:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setAuditRunning(false);
    }
  };

  const markNotApplicable = async (domain, reason, userEmail) => {
    const { error: err } = await supabase
      .from('email_domain_observations')
      .update({
        tracking_status: 'not_applicable',
        not_applicable_reason: reason,
        empresa_id: null,
        updated_by: userEmail || 'ui',
        updated_at: new Date().toISOString(),
      })
      .eq('domain', domain);
    if (err) return { success: false, error: err.message };
    await fetchRows();
    return { success: true };
  };

  // Propaga domínio→empresa em `dominios_empresa` (fonte autoritativa).
  // Erro é logado mas não quebra o fluxo: email_domain_observations já foi atualizada.
  const propagateDominioEmpresa = useCallback(async ({ dominio, empresa_id, tipo_empresa, by }) => {
    if (!dominio || !empresa_id || !tipo_empresa) {
      return { error: { message: 'propagateDominioEmpresa: dominio, empresa_id e tipo_empresa são obrigatórios' } };
    }
    const { error } = await supabase
      .from('dominios_empresa')
      .upsert(
        { dominio, empresa_id, tipo_empresa, atualizado_por: by || 'ui' },
        { onConflict: 'dominio', ignoreDuplicates: false }
      );
    if (error) {
      console.warn('propagateDominioEmpresa failed:', error.message);
    }
    return { error };
  }, []);

  // Busca tipo_empresa de outra entrada da mesma empresa (se houver) para pré-preencher dropdown
  const fetchTipoEmpresaDefault = useCallback(async (empresaId) => {
    if (!empresaId) return 'developer';
    const { data } = await supabase
      .from('dominios_empresa')
      .select('tipo_empresa')
      .eq('empresa_id', empresaId)
      .limit(1)
      .maybeSingle();
    return data?.tipo_empresa || 'developer';
  }, []);

  const linkToExistingEmpresa = async (domain, empresaId, userEmail, tipoEmpresa) => {
    const { error: err } = await supabase
      .from('email_domain_observations')
      .update({
        tracking_status: 'tracked',
        empresa_id: empresaId,
        not_applicable_reason: null,
        updated_by: userEmail || 'ui',
        updated_at: new Date().toISOString(),
      })
      .eq('domain', domain);
    if (err) return { success: false, error: err.message };

    // Propaga em dominios_empresa quando o chamador informou tipoEmpresa
    if (tipoEmpresa) {
      await propagateDominioEmpresa({
        dominio: domain,
        empresa_id: empresaId,
        tipo_empresa: tipoEmpresa,
        by: userEmail || 'ui:link_existing',
      });
    }

    await fetchRows();
    return { success: true };
  };

  const createEmpresaFromDomain = async (domain, { company_name, website, tipo_empresa }, userEmail) => {
    // Convenção MAIÚSCULO para company_name e empresa (formalizada 22/abr/2026)
    const nameUpper = (company_name || domain).toUpperCase();
    const { data: newEmp, error: insErr } = await supabase
      .from('empresas')
      .insert({
        empresa: nameUpper,
        company_name: nameUpper,
        website: website || `https://${domain}`,
        updated_by: userEmail || 'ui:create_from_domain',
      })
      .select('id, empresa')
      .single();
    if (insErr) return { success: false, error: insErr.message };

    const { error: upErr } = await supabase
      .from('email_domain_observations')
      .update({
        tracking_status: 'tracked',
        empresa_id: newEmp.id,
        not_applicable_reason: null,
        updated_by: userEmail || 'ui:create_from_domain',
        updated_at: new Date().toISOString(),
      })
      .eq('domain', domain);
    if (upErr) return { success: false, error: upErr.message, empresa: newEmp };

    // Propaga em dominios_empresa com tipo_empresa informado (obrigatório no modal)
    if (tipo_empresa) {
      await propagateDominioEmpresa({
        dominio: domain,
        empresa_id: newEmp.id,
        tipo_empresa,
        by: userEmail || 'ui:create_from_domain',
      });
    }

    await fetchRows();
    return { success: true, empresa: newEmp };
  };

  const undoLink = async (domain, userEmail) => {
    const { error: err } = await supabase
      .from('email_domain_observations')
      .update({
        tracking_status: 'pending',
        empresa_id: null,
        not_applicable_reason: null,
        updated_by: userEmail || 'ui:undo',
        updated_at: new Date().toISOString(),
      })
      .eq('domain', domain);
    if (err) return { success: false, error: err.message };
    await fetchRows();
    return { success: true };
  };

  const reevaluate = async (domain, userEmail) => undoLink(domain, userEmail);

  return {
    rows,
    loading,
    error,
    auditRunning,
    lastRunSummary,
    refetch: fetchRows,
    runAudit,
    markNotApplicable,
    linkToExistingEmpresa,
    createEmpresaFromDomain,
    undoLink,
    reevaluate,
    propagateDominioEmpresa,
    fetchTipoEmpresaDefault,
  };
};
